// src/components/Navbar.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaBars, FaTimes } from "react-icons/fa";

const reflexLogo = "https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/reflex.png";

const menuData = [
  {
    id: 1,
    title: "Company",
    path: "/",
    dropdown: [
      { id: 1, title: "About Us", path: "/about-us" },
      { id: 2, title: "Services", path: "/services" },
      { id: 3, title: "Industries", path: "/industries" },
      { id: 4, title: "Podcast", path: "/podcast" },
    ],
  },

   {
    id: 2,
    title: "Offer",
    dropdown: [
      { id: 1, title: "Website Offer", path: "/", hash: "offer-section" },
      { id: 2, title: "Client Acquisition System", path: "/", hash: "client-acquisition" },
      { id: 3, title: "Reflex Revival System", path: "/", hash: "reflex-revival" },
    ],
  },

  {
    id: 3,
    title: "Resources",
    dropdown: [
      { id: 1, title: "Free Downloads", path: "/free-downloads" },
      { id: 2, title: "Free Playbook", path: "/sales-playbook" },
      { id: 3, title: "Blog", path: "/blog" },
    ],
  },
 
  { id: 4, title: "Payment", path: "/payment" },
  { id: 5, title: "Find Businesses", path: "/businesses" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openIndex, setOpenIndex] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);

    const handleClickOutside = (event) => {
      if (
        window.innerWidth > 992 &&
        navRef.current &&
        !navRef.current.contains(event.target)
      ) {
        setOpenIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setOpenIndex(-1);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");

    let attempts = 0;
    const maxAttempts = 40;
    let timer;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        timer = setTimeout(tryScroll, 100);
      }
    };

    tryScroll();
    return () => clearTimeout(timer);
  }, [location]);

  const toggleDropdown = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
    setOpenIndex(-1);
  };

  const handleDropdownLinkClick = (e, drop) => {
    if (drop.hash) {
      e.preventDefault();
      setOpenIndex(-1);
      setMobileOpen(false);
      if (currentPath === drop.path) {
        const el = document.getElementById(drop.hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `${drop.path}#${drop.hash}`);
      } else {
        navigate(`${drop.path}#${drop.hash}`);
      }
    } else {
      setOpenIndex(-1);
      setMobileOpen(false);
    }
  };

  return (
    <>
      <nav
        ref={navRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 9999,
          padding: scrolled ? "10px 0" : "16px 0",
          background: scrolled ? "rgba(0,0,0,0.95)" : "rgba(0,0,0,0.85)",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          boxShadow: scrolled ? "0 4px 12px rgba(0,0,0,0.2)" : "none",
          transition: "all 0.3s ease",
          fontFamily: "Elza, sans-serif",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1150px",
            padding: "0 16px",
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
            <img src={reflexLogo} alt="Reflex Logo" style={{ height: "24px", cursor: "pointer" }} />
          </Link>

          {/* Hamburger */}
          <div
            className="mobile-menu-icon"
            onClick={toggleMobileMenu}
            style={{
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
              display: "none",
              flexShrink: 0,
            }}
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </div>

          {/* Desktop Menu */}
          <ul
            className="nav-menu"
            style={{
              listStyle: "none",
              display: "flex",
              alignItems: "center",
              gap: "25px",
              margin: 0,
              padding: 0,
            }}
          >
            {menuData.map((item, index) => {
              const isActive =
                item.path === currentPath ||
                (item.dropdown && item.dropdown.some((d) => d.path === currentPath && !d.hash));

              return (
                <li key={item.id} style={{ position: "relative" }}>
                  {item.dropdown ? (
                    <div
                      onClick={() => toggleDropdown(index)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#fff",
                        fontSize: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                        padding: "6px 0",
                        borderBottom: isActive ? "3px solid #7b19bc" : "3px solid transparent",
                        transition: "border-bottom 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>{item.title}</span>
                      <FaChevronDown
                        size={12}
                        style={{
                          transform: openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      style={{
                        display: "block",
                        color: "#fff",
                        fontSize: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                        padding: "6px 0",
                        textDecoration: "none",
                        borderBottom: isActive ? "3px solid #7b19bc" : "3px solid transparent",
                        transition: "border-bottom 0.3s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.title}
                    </Link>
                  )}

                  {item.dropdown && openIndex === index && (
                    <ul
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                        position: "absolute",
                        top: "40px",
                        left: 0,
                        background: "#111",
                        borderRadius: "6px",
                        minWidth: "220px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                        zIndex: 999,
                      }}
                    >
                      {item.dropdown.map((drop) => {
                        const dropActive = drop.path === currentPath && !drop.hash;
                        return (
                          <li key={drop.id}>
                            <Link
                              to={drop.hash ? `${drop.path}#${drop.hash}` : drop.path}
                              style={{
                                display: "block",
                                padding: "10px 20px",
                                fontSize: "15px",
                                color: "#fff",
                                textDecoration: "none",
                                borderBottom: dropActive ? "3px solid #7b19bc" : "none",
                                transition: "background 0.3s ease",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                              }}
                              onClick={(e) => handleDropdownLinkClick(e, drop)}
                            >
                              {drop.title}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Desktop Right Buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <Link
              to="/signup"
              className="signup-btn-desktop"
              style={{
                padding: "8px 18px",
                fontSize: "12px",
                fontWeight: "bold",
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                textDecoration: "none",
                borderRadius: "0px",
                whiteSpace: "nowrap",
              }}
            >
              Sign Up
            </Link>

            <Link
              to="/contact-us"
              className="contact-btn-desktop"
              style={{
                padding: "8px 18px",
                fontSize: "12px",
                fontWeight: "bold",
                background: "#7b19bc",
                color: "#fff",
                textDecoration: "none",
                borderRadius: "0px",
                whiteSpace: "nowrap",
              }}
            >
              Contact
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div
          className="mobile-drawer"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            background: "rgba(0,0,0,0.97)",
            zIndex: 9998,
            overflowY: "auto",
            padding: "80px 24px 40px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {menuData.map((item, index) => {
            const isActive =
              item.path === currentPath ||
              (item.dropdown && item.dropdown.some((d) => d.path === currentPath && !d.hash));

            return (
              <div key={item.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                {item.dropdown ? (
                  <>
                    <div
                      onClick={() => toggleDropdown(index)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "#fff",
                        fontSize: "17px",
                        fontWeight: 500,
                        cursor: "pointer",
                        padding: "14px 0",
                        borderLeft: isActive ? "3px solid #7b19bc" : "3px solid transparent",
                        paddingLeft: "10px",
                      }}
                    >
                      <span>{item.title}</span>
                      <FaChevronDown
                        size={12}
                        style={{
                          transform: openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </div>
                    {openIndex === index && (
                      <div style={{ paddingLeft: "16px", paddingBottom: "8px" }}>
                        {item.dropdown.map((drop) => (
                          <Link
                            key={drop.id}
                            to={drop.hash ? `${drop.path}#${drop.hash}` : drop.path}
                            style={{
                              display: "block",
                              padding: "10px 0",
                              fontSize: "15px",
                              color:
                                drop.path === currentPath && !drop.hash ? "#9f3ee5" : "#ccc",
                              textDecoration: "none",
                            }}
                            onClick={(e) => handleDropdownLinkClick(e, drop)}
                          >
                            {drop.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    style={{
                      display: "block",
                      color: "#fff",
                      fontSize: "17px",
                      fontWeight: 500,
                      padding: "14px 0 14px 10px",
                      textDecoration: "none",
                      borderLeft: isActive ? "3px solid #7b19bc" : "3px solid transparent",
                    }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            );
          })}

          {/* Mobile Sign Up */}
          <Link
            to="/signup"
            style={{
              display: "block",
              marginTop: "12px",
              padding: "14px 0",
              fontSize: "16px",
              fontWeight: "bold",
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "4px",
              textAlign: "center",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
            onClick={() => setMobileOpen(false)}
          >
            Sign Up
          </Link>

          <Link
            to="/contact-us"
            style={{
              display: "block",
              marginTop: "10px",
              padding: "14px 0",
              fontSize: "16px",
              fontWeight: "bold",
              background: "#7b19bc",
              color: "#fff",
              textAlign: "center",
              textDecoration: "none",
              borderRadius: "4px",
              boxSizing: "border-box",
            }}
            onClick={() => setMobileOpen(false)}
          >
            Contact
          </Link>
        </div>
      )}

      <style>{`
        .mobile-menu-icon {
          display: none !important;
        }
        .contact-btn-desktop,
        .signup-btn-desktop {
          display: block;
        }
        .signup-btn-desktop {
          transition: all 0.25s ease;
        }
        .signup-btn-desktop:hover {
          background: rgba(255,255,255,0.1) !important;
          border-color: rgba(255,255,255,0.6) !important;
        }
        .contact-btn-desktop {
          transition: all 0.25s ease;
          position: relative;
          overflow: hidden;
        }
        .contact-btn-desktop::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.15);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .contact-btn-desktop:hover::after {
          opacity: 1;
        }
        .nav-menu li a, .nav-menu li div {
          transition: color 0.2s ease;
        }
        .nav-menu li a:hover {
          color: #c084fc !important;
        }
        @media (max-width: 992px) {
          .mobile-menu-icon {
            display: flex !important;
            align-items: center;
          }
          .nav-menu {
            display: none !important;
          }
          .contact-btn-desktop,
          .signup-btn-desktop {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default Navbar;